// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './DeliveryImageView.scss';
import { useTranslation } from 'react-i18next';

import BaleCard from '../components/BaleCard/BaleCard.jsx';
import LoadingIcon from '../components/LoadingIcon/LoadingIcon.jsx';
import HelpModal from '../components/HelpModal/HelpModal.jsx';
import SwipeableContainer from '../components/SwipeableContainer/SwipeableContainer.jsx';
import BBoxImageView from '../components/BBoxImageView/BBoxImageView.jsx';

import { fetchImageUrl } from '../shared_components/utils/ImgUtils';
import { firestore, functions, storage } from '../context/firebase';
import { onSnapshot, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import ImageQualityBanner from '../components/ImageQualityBanner/ImageQualityBanner.jsx';

// Importing icons
import back_icon from '../assets/icons/white/back_small.png';
import alert_off_icon from '../assets/icons/white/alert_no_circle.png';
import alert_on_icon from '../assets/icons/red/alert.png';
import { toast } from 'sonner';
import { BALE_DOCUMENT_PATH, DELIVERY_IMAGE_DOCUMENT_PATH } from '../shared_components/utils/FirebaseUtils.js';
import { useAuthStore } from '../stores/authStore.js';
import CommonModal from '../shared_components/CommonModal/CommonModal.jsx';
import { useGetDocument } from '../shared_components/hooks/useGetDocument.js';
import { useSetDocument } from '../shared_components/hooks/useSetDocument.js';
import { getLabelDisplayName } from '../shared_components/utils/FirebaseLabelsetsUtils.js';

export default function DeliveryImageView({
  deliveryId,
  deliveryImageId,
  returnToDelivery,
  deleteDeliveryImage,
  importBales,
  deliveryImages,
  setActiveDeliveryImageId,
}) {
  const { i18n, t } = useTranslation();
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const globalLabelsets = useAuthStore((state) => state.globalLabelsets);
  const [deliveryImageData, setDeliveryImageData] = React.useState(null);
  const [showDeletePopup, setShowDeletePopup] = React.useState(false);
  const [showHelpModal, setShowHelpModal] = React.useState(false);
  const [baleImageQuality, setBaleImageQuality] = React.useState({});
  const baleQualityUnsubscribes = React.useRef({});

  const deliveryImageDocRef = doc(
    firestore,
    DELIVERY_IMAGE_DOCUMENT_PATH(currentUserClientId, deliveryId, deliveryImageId),
  );
  const { data: deliveryImageDoc } = useGetDocument(deliveryImageDocRef, { withSnapshot: true });
  const { setDocument: setDeliveryImageDoc } = useSetDocument(deliveryImageDocRef);

  React.useEffect(() => {
    if (!deliveryImageDoc || !deliveryImageDoc.image_storage_path) {
      return;
    }

    setDeliveryImageData(null);
    fetchImageUrl(storage, deliveryImageDoc.image_storage_path).then((url) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        setDeliveryImageData(img);
      };
    });
  }, [deliveryImageDoc?.image_storage_path]);

  // Subscribe to image quality data for detected bales in this delivery image
  React.useEffect(() => {
    if (!deliveryImageDoc?.detected_bales || !currentUserClientId) {
      // Clean up existing subscriptions
      Object.values(baleQualityUnsubscribes.current).forEach((unsubscribe) => unsubscribe());
      baleQualityUnsubscribes.current = {};
      setBaleImageQuality({});
      return;
    }

    const detectedBaleIds = Object.keys(deliveryImageDoc.detected_bales);

    // Clean up old subscriptions
    Object.values(baleQualityUnsubscribes.current).forEach((unsubscribe) => unsubscribe());
    baleQualityUnsubscribes.current = {};

    // Subscribe to each detected bale's image quality
    detectedBaleIds.forEach((baleId) => {
      const baleDocRef = doc(firestore, BALE_DOCUMENT_PATH(currentUserClientId, deliveryId, baleId));

      const unsubscribe = onSnapshot(baleDocRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const imageIssues = data?.predicted_labels?.image_issues?.class;

          setBaleImageQuality((prev) => ({
            ...prev,
            [baleId]: imageIssues,
          }));
        } else {
          setBaleImageQuality((prev) => {
            const newState = { ...prev };
            delete newState[baleId];
            return newState;
          });
        }
      });

      baleQualityUnsubscribes.current[baleId] = unsubscribe;
    });

    return () => {
      Object.values(baleQualityUnsubscribes.current).forEach((unsubscribe) => unsubscribe());
      baleQualityUnsubscribes.current = {};
    };
  }, [deliveryImageDoc?.detected_bales, deliveryId, currentUserClientId]);

  // Calculate image quality issues for this delivery image
  const getImageQualityIssues = () => {
    if (!deliveryImageDoc?.detected_bales)
      return {
        hasIssues: false,
        balesWithIssues: [],
        uniqueIssues: [],
        selectedBalesWithIssues: [],
        allAffectedBalesUnselected: false,
      };

    const detectedBaleIds = Object.keys(deliveryImageDoc.detected_bales);
    const balesWithIssues = detectedBaleIds.filter((baleId) => {
      const quality = baleImageQuality[baleId];
      return quality && quality !== 'no_issues';
    });

    // Check which bales with issues are selected
    const selectedBalesWithIssues = balesWithIssues.filter((baleId) => {
      return deliveryImageDoc.detected_bales[baleId]?.selected === true;
    });

    // Determine if all affected bales are unselected
    const allAffectedBalesUnselected = balesWithIssues.length > 0 && selectedBalesWithIssues.length === 0;

    // Extract unique issues
    const allIssues = [];
    balesWithIssues.forEach((baleId) => {
      const quality = baleImageQuality[baleId];
      if (Array.isArray(quality)) {
        allIssues.push(...quality);
      } else {
        allIssues.push(quality);
      }
    });

    const uniqueIssues = [...new Set(allIssues)].map((issue) => {
      const issueDisplayName =
        getLabelDisplayName(globalLabelsets?.image_issues, issue, i18n.language) ||
        issue.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      return issueDisplayName;
    });

    return {
      hasIssues: balesWithIssues.length > 0,
      balesWithIssues,
      uniqueIssues,
      totalBales: detectedBaleIds.length,
      selectedBalesWithIssues,
      allAffectedBalesUnselected,
    };
  };

  const imageQualityInfo = getImageQualityIssues();

  function toggleBaleSelection(baleId) {
    setDeliveryImageDoc(
      {
        detected_bales: {
          [baleId]: { selected: !deliveryImageDoc.detected_bales[baleId].selected },
        },
      },
      { merge: true },
    );
  }

  function scrollDeliveryImage(scrollAmount) {
    /* Change the displayed image by scrolling through scrollAmount images. */
    if (!deliveryImageId || !scrollAmount) return;

    const deliveryImageIds = Object.keys(deliveryImages).sort();
    const currentIndex = deliveryImageIds.indexOf(deliveryImageId);
    if (currentIndex === -1) return;

    const nextIndex = currentIndex + scrollAmount;
    if (nextIndex >= deliveryImageIds.length || nextIndex < 0) return;
    const nextDeliveryImageId = deliveryImageIds[nextIndex];
    setActiveDeliveryImageId(nextDeliveryImageId);
  }

  function toggleFlagged() {
    /* Toggle the flagged status of the delivery image */
    setDeliveryImageDoc(
      {
        flagged: !deliveryImageDoc?.flagged,
      },
      { merge: true },
    );
  }

  async function recomputeBaleDetection() {
    /* Recompute bale detection for the current delivery image */
    const recomputeBaleDetection = httpsCallable(functions, 'on_request_delivery_image_processing');
    try {
      await recomputeBaleDetection({
        client_id: currentUserClientId,
        delivery_id: deliveryId,
        delivery_image_id: deliveryImageId,
      });
    } catch (error) {
      console.error(error);
    }
  }

  if (!deliveryImageDoc) {
    <div className='delivery-image-view'>
      <div className='delivery-image-view--header'>
        <div onClick={returnToDelivery} className='delivery-image-view--back'>
          <img src={back_icon} alt='Back' className='delivery-image-view--back-icon' />
        </div>
      </div>
      {/* Loading spinner */}
      <LoadingIcon />
    </div>;
  }

  return (
    <div className='delivery-image-view'>
      <div className='delivery-image-view--header'>
        <div onClick={returnToDelivery} className='delivery-image-view--back'>
          <img src={back_icon} alt='Back' className='delivery-image-view--back-icon' />
        </div>
        <button onClick={() => setShowDeletePopup(true)} className='delivery-image-view--delete'>
          {t('general_delete')}
        </button>
      </div>
      <div className='delivery-image-view--img-container'>
        <SwipeableContainer
          style={{ width: '100%', height: '100%' }}
          onSwipeLeft={() => scrollDeliveryImage(-1)}
          onSwipeRight={() => scrollDeliveryImage(1)}
          resetKey={deliveryImageId}
        >
          {deliveryImageData && (
            <BBoxImageView
              image={{ src: deliveryImageData.src, width: deliveryImageData.width, height: deliveryImageData.height }}
              boundingBoxes={deliveryImageDoc?.detected_bales ?? {}}
              onBBoxClicked={toggleBaleSelection}
              style={{ width: '100%', height: '100%' }}
              baleImageQuality={baleImageQuality}
            />
          )}
          {!deliveryImageData && (
            <div className='loading-icon-container'>
              <LoadingIcon />
            </div>
          )}

          <div
            className='delivery-image-alert'
            onClick={toggleFlagged}
            style={{ opacity: deliveryImageDoc?.flagged ? 1 : 0.5, display: deliveryImageData ? 'block' : 'none' }}
          >
            <img src={deliveryImageDoc?.flagged ? alert_on_icon : alert_off_icon} />
          </div>

          {/* {deliveryImageDoc?.detected_bales &&<div className="help" onClick={() => setShowHelpModal(true)}>?</div>} */}
        </SwipeableContainer>
      </div>

      {/* {imageQualityInfo.hasIssues && (
                <div className={`delivery-image-view--quality-banner ${imageQualityInfo.allAffectedBalesUnselected ? 'collapsed' : ''}`}>
                    {imageQualityInfo.allAffectedBalesUnselected ? (
                        // Collapsed warning when all affected bales are unselected
                        <div className="delivery-image-view--quality-banner-collapsed">
                            <div className="delivery-image-view--quality-banner-icon collapsed">
                                <AlertTriangle size={16} />
                            </div>
                            <div className="delivery-image-view--quality-banner-collapsed-content">
                                <span className="delivery-image-view--quality-banner-collapsed-title">
                                    {t("image_quality_warning_short")}
                                </span>
                                <span className="delivery-image-view--quality-banner-collapsed-status">
                                    {t("affected_images_skipped")}
                                </span>
                            </div>
                        </div>
                    ) : (
                        // Full warning banner when there are selected bales with issues
                        <>
                            <div className="delivery-image-view--quality-banner-content">
                                <div className="delivery-image-view--quality-banner-icon">
                                    <AlertTriangle size={18} />
                                </div>
                                <div className="delivery-image-view--quality-banner-info">
                                    <div className="delivery-image-view--quality-banner-title">
                                        {t("image_quality_warning")}
                                    </div>
                                    <div className="delivery-image-view--quality-banner-details">
                                        <span className="delivery-image-view--quality-banner-count">
                                            {t("bales_selected_with_issues", { 
                                                count: imageQualityInfo.selectedBalesWithIssues.length, 
                                                total: imageQualityInfo.totalBales 
                                            })}
                                        </span>
                                        {imageQualityInfo.uniqueIssues.length > 0 && (
                                            <span className="delivery-image-view--quality-banner-separator">•</span>
                                        )}
                                        <span className="delivery-image-view--quality-banner-issues">
                                            {imageQualityInfo.uniqueIssues.join(", ")}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="delivery-image-view--quality-banner-action">
                                <span className="delivery-image-view--quality-banner-suggestion">
                                    {t("quality_issues_suggestion")}
                                </span>
                            </div>
                        </>
                    )}
                </div>
            )} */}

      {/* Buttons to jump to previous/next delivery image */}
      <div className='action-footer-wrapper'>
        {/* Image Quality Issues Notification Card */}
        {imageQualityInfo.hasIssues && !imageQualityInfo.allAffectedBalesUnselected && (
          <div className='delivery-image-view--quality-banner-container'>
            <ImageQualityBanner
              hasIssues={imageQualityInfo.hasIssues}
              selectedCount={imageQualityInfo.selectedBalesWithIssues.length}
              total={imageQualityInfo.totalBales}
              issues={imageQualityInfo.uniqueIssues}
              allAffectedBalesUnselected={imageQualityInfo.allAffectedBalesUnselected}
            />
          </div>
        )}

        <button className='delivery-image-swipe-button prev' onClick={() => scrollDeliveryImage(-1)}>
          <img src={back_icon} />
        </button>

        <div className='action-footer'>
          {deliveryImageDoc?.detected_bales && (
            <p className='detected-bales-count'>{`${Object.keys(deliveryImageDoc.detected_bales).length} ${t('delivery_bales_detected')}`}</p>
          )}
          {deliveryImageDoc?.detected_bales && (
            <button
              className='import-button'
              onClick={() => {
                importBales(deliveryImageDoc?.detected_bales);
                returnToDelivery();
                toast.success(t('delivery_import_success'), { duration: 3000, dismissible: true });
              }}
            >
              {t('delivery_import')}
            </button>
          )}
        </div>

        <button className='delivery-image-swipe-button next' onClick={() => scrollDeliveryImage(1)}>
          <img src={back_icon} />
        </button>
      </div>

      <div
        className={`bale-images ${
          (Object.keys(deliveryImageDoc?.detected_bales ?? {}).length === 0 &&
            deliveryImageDoc?.bale_detection_status === 'success') ||
          deliveryImageDoc?.bale_detection_status === 'error' ||
          (!deliveryImageDoc?.detected_bales && !['success', 'error'].includes(deliveryImageDoc?.bale_detection_status))
            ? 'compact'
            : ''
        }`}
      >
        <div className='bale-images-wrapper'>
          {deliveryImageDoc?.detected_bales &&
            Object.entries(deliveryImageDoc.detected_bales)
              .sort()
              .map(([baleId, bale], index) => {
                return (
                  <div
                    onClick={() => {
                      toggleBaleSelection(baleId);
                    }}
                    key={index}
                  >
                    <BaleCard
                      key={index}
                      imageStoragePath={bale.image_storage_path}
                      selected={bale.selected}
                      baleId={baleId}
                      deliveryId={deliveryId}
                      isLoading={bale.processing_status === 'running'}
                    />
                  </div>
                );
              })}
        </div>
        {!deliveryImageDoc?.detected_bales &&
          !['success', 'error'].includes(deliveryImageDoc?.bale_detection_status) && (
            <div className='loading-icon-container'>
              <LoadingIcon />
            </div>
          )}
        {Object.keys(deliveryImageDoc?.detected_bales ?? {}).length === 0 &&
          deliveryImageDoc?.bale_detection_status === 'success' && (
            <p className='no-bales-detected'>{t('delivery_bale_detection_no_bales_detected')}</p>
          )}
        {deliveryImageDoc?.bale_detection_status === 'error' && (
          <div className='error-container'>
            <p className='error'>{t('delivery_bale_detection_error')}</p>
            <button className='recompute-button' onClick={recomputeBaleDetection}>
              {t('delivery_recompute_detection')}
            </button>
          </div>
        )}
      </div>

      {/* Popup for confirming deletion of delivery image */}
      <CommonModal
        show={showDeletePopup}
        title={t('delivery_confirm_delete_image')}
        confirmText={t('general_delete')}
        cancelText={t('general_cancel')}
        onConfirm={() => {
          deleteDeliveryImage(deliveryImageId);
          setShowDeletePopup(false);
        }}
        onCancel={() => setShowDeletePopup(false)}
        theme='dark'
        topCloseButton
        containerClassName='text-center'
        variant='danger'
      >
        <>
          {t('delivery_confirm_delete_image_description')}
          {Object.keys(deliveryImageDoc?.detected_bales ?? {}).length > 0 && (
            <p>{t('delivery_confirm_delete_image_info')}</p>
          )}
        </>
      </CommonModal>

      {/* Popup for help/tutorial */}
      <HelpModal
        show={showHelpModal}
        onHide={() => setShowHelpModal(false)}
        videoUrl={`https://www.youtube.com/embed/9xwazD5SyVg?autoplay=1&loop=1&playlist=9xwazD5SyVg`}
        modalTitle={t('instructions_import_bales')}
      />
    </div>
  );
}
